/*
 * MTG singles: the catalog, the browse page, and the live cards inside magazine articles.
 *
 * The whole catalog comes from templates/collection.wgt-singles-data.liquid (250 cards a page) and is
 * searched, filtered and sorted here, in the browser. Every filter is in the page address, so a
 * filtered view is a link anyone can share and an article can point to.
 *
 * Three things on a page turn this on:
 *   [data-ws-browser]   the browse page (sections/wgt-singles-browser.liquid)
 *   .wgt-rail           a link to a filtered view; becomes a row of the cards in stock that match
 *   .wgt-spot           a block about one card; gets the card's picture, price and an Add button
 * Inside [data-ws-article], links to single products also get a hover preview and are collected
 * into the "Cards in this story" box ([data-ws-story]).
 */
(function () {
  'use strict';
  if (window.WGTSingles) return;

  var cfg = window.WGTSinglesConfig || {};
  var COLLECTION = cfg.collectionUrl || '/collections/magic-the-gathering-singles';
  var CART_ADD = (cfg.cartAddUrl || '/cart/add') + '.js';
  var CART_URL = cfg.cartUrl || '/cart';
  var PAGE = 48;
  var CACHE_KEY = 'wgt-singles:' + COLLECTION;
  var CACHE_MS = 2 * 60 * 1000;

  var COLOR = { white: 'w', blue: 'u', black: 'b', red: 'r', green: 'g' };
  var COLOR_NAME = { w: 'White', u: 'Blue', b: 'Black', r: 'Red', g: 'Green', c: 'Colorless', m: 'Multicolor' };
  var WUBRG = 'wubrg';
  var RARITIES = ['mythic', 'rare', 'uncommon', 'common'];
  var TYPES = ['creature', 'instant', 'sorcery', 'artifact', 'enchantment', 'planeswalker', 'land', 'battle'];
  var FORMATS = ['standard', 'pioneer', 'modern', 'legacy', 'vintage', 'commander', 'pauper'];
  var PRICES = [['0-0.5', 'Under 50¢'], ['0.5-1', '50¢ to $1'], ['1-5', '$1 to $5'], ['5-', '$5 and up']];
  var FLAGS = [['commander', 'Can be your commander'], ['legendary', 'Legendary'], ['staple', 'Commander staple'], ['borderless', 'Borderless']];
  var SORTS = [['featured', 'Featured'], ['price-asc', 'Price: low to high'], ['price-desc', 'Price: high to low'], ['name', 'Name: A to Z'], ['newest', 'Newest'], ['mv', 'Mana value']];
  var DOES = {
    'removal': 'Removal', 'tokens': 'Token makers', 'draw': 'Card draw', 'recursion': 'Graveyard recursion', 'burn': 'Burn',
    'ramp': 'Ramp', 'mill': 'Mill', 'lifegain': 'Lifegain', 'reanimate': 'Reanimation', 'copy': 'Copy effects',
    'boardwipe': 'Board wipes', 'tutor': 'Tutors', 'dual-land': 'Dual lands', 'theft': 'Theft', 'anthem': 'Anthems',
    'graveyard-hate': 'Graveyard hate', 'bounce': 'Bounce', 'counterspell': 'Counterspells', 'blink': 'Blink',
    'mana-dork': 'Mana creatures', 'mana-rock': 'Mana rocks', 'wheel': 'Wheels', 'clone': 'Clones'
  };
  var LIST_KEYS = ['color', 'type', 'rarity', 'legal', 'set', 'finish', 'mv', 'does', 'flag', 'condition', 'lang'];
  var ALL_OF = { legal: true, flag: true };

  // The games, as in plans/2026-10-multi-game-singles.md (wgt-strategy). The name is the one in product titles:
  // "<Name> - <Set> #<number> - <name> Single". Rarities are the known ones in the order to list them; any other
  // rarity tag still shows, after these.
  var GAMES = {
    mtg: { name: 'Magic: The Gathering', finishes: [['nonfoil', 'Nonfoil'], ['foil', 'Foil'], ['etched', 'Etched foil']] },
    pokemon: { name: 'Pokémon', finishes: [['nonfoil', 'Nonfoil'], ['holofoil', 'Holofoil'], ['reverse-holofoil', 'Reverse Holofoil']],
      rarities: ['common', 'uncommon', 'rare', 'holo-rare', 'double-rare', 'ace-spec-rare', 'ultra-rare', 'illustration-rare', 'special-illustration-rare', 'hyper-rare', 'shiny-rare', 'shiny-ultra-rare', 'secret-rare', 'promo'] },
    lorcana: { name: 'Disney Lorcana', finishes: [['nonfoil', 'Nonfoil'], ['foil', 'Foil']],
      rarities: ['common', 'uncommon', 'rare', 'super-rare', 'legendary', 'epic', 'enchanted', 'iconic', 'promo'] },
    onepiece: { name: 'One Piece Card Game', finishes: [['nonfoil', 'Nonfoil'], ['foil', 'Foil']],
      rarities: ['leader', 'common', 'uncommon', 'rare', 'super-rare', 'secret-rare', 'special-card', 'treasure-rare', 'promo'] },
    riftbound: { name: 'Riftbound', finishes: [['nonfoil', 'Nonfoil'], ['foil', 'Foil']],
      rarities: ['common', 'uncommon', 'rare', 'epic', 'showcase', 'overnumbered', 'promo'] }
  };
  var GAME = GAMES[cfg.game] ? cfg.game : 'mtg'; // the game of the browse page this script is on
  var CONDITIONS = ['near-mint', 'lightly-played', 'moderately-played', 'heavily-played', 'damaged'];
  // A printing's language, as the listing tags it (`japanese`); a listing with none of these is English.
  // The card show kiosk reads the same tags (LANGUAGES in companion/api/src/kiosk-singles.ts, wgt-mobile).
  var LANGUAGES = ['japanese', 'german', 'french', 'spanish', 'italian', 'portuguese', 'korean', 'russian', 'chinese-simplified', 'chinese-traditional', 'phyrexian'];

  // A tag that is a rarity, on a game other than Magic (Magic's are RARITIES): known to the game, or ending in "rare".
  function isRarity(t, game) {
    if (t === 'single' || t === game || t.indexOf(game + '-') === 0 || t.indexOf('batch-') === 0 || LANGUAGES.indexOf(t) > -1) return false;
    return (GAMES[game].rarities || []).indexOf(t) > -1 || /(^|-)rare$/.test(t);
  }
  function gameOf(r) { // custom.game, else the "<key>-single" tag, else Magic
    if (r.m && GAMES[r.m]) return r.m;
    var tags = ',' + r.g.replace(/\s*,\s*/g, ',') + ',';
    for (var k in GAMES) if (k !== 'mtg' && tags.indexOf(',' + k + '-single,') > -1) return k;
    return 'mtg';
  }
  function reRaw(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

  /* ---------- small helpers ---------- */
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function slug(s) { return String(s).toLowerCase().replace(/['’]/g, '-').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }
  function words(s) { return s.replace(/-/g, ' ').replace(/^./, function (c) { return c.toUpperCase(); }); }
  function money(cents) { return cents < 100 ? cents + '¢' : '$' + (cents / 100).toFixed(2); }
  function plain(s) { return String(s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, ''); }
  function img(url, w) { return url.replace(/width=\d+/, 'width=' + w); }
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /* ---------- the catalog ---------- */
  var cards = [], byHandle = {}, setNames = {}, catalogPromise = null, inCart = {};

  function parse(r, index) {
    var game = gameOf(r);
    var c = { index: index, game: game, handle: r.h, url: '/products/' + r.h, img: r.i, created: r.c, colors: [], identity: [], types: [], sub: [], legal: [], does: [], keywords: [], rarity: game === 'mtg' ? 'rare' : '', lang: 'english', mv: null, edh: 0, commander: false, legendary: false, borderless: false };
    var path = r.y.split(' > ');
    c.set = path.length > 3 ? path[2] : '';
    var cut = r.t.lastIndexOf(' - ' + c.set + ' #');
    var tail = ' - ' + reRaw(GAMES[game].name) + ' Single$';
    var m = new RegExp('#(\\S+)' + tail).exec(r.t) || (game === 'mtg' ? null : /#(\S+) - [^#]+ Single$/.exec(r.t));
    c.name = cut > 0 ? r.t.slice(0, cut) : r.t.replace(new RegExp(tail), '');
    if (cut <= 0 && game !== 'mtg') c.name = c.name.replace(/ - [^-#]+ Single$/, '');
    c.num = m ? m[1] : '';
    c.setKey = slug(c.set);
    c.code = (r.k || '').split('-')[1] || '';
    r.g.split(',').forEach(function (t) {
      t = t.trim();
      if (t.indexOf('mtg-') === 0) {
        t = t.slice(4);
        var dash = t.indexOf('-'), kind = t.slice(0, dash), val = t.slice(dash + 1);
        if (kind === 'color') { if (COLOR[val]) c.colors.push(COLOR[val]); }
        else if (kind === 'identity') { if (COLOR[val]) c.identity.push(COLOR[val]); }
        else if (kind === 'type') c.types.push(val);
        else if (kind === 'subtype') c.sub.push(val);
        else if (kind === 'supertype') { if (val === 'legendary') c.legendary = true; }
        else if (kind === 'legal') c.legal.push(val);
        else if (kind === 'mv') c.mv = parseInt(val, 10);
        else if (kind === 'does') c.does.push(val);
        else if (kind === 'keyword') c.keywords.push(val);
        else if (kind === 'edhrec') { var n = parseInt(val.replace('top-', ''), 10); if (n && (!c.edh || n < c.edh)) c.edh = n; }
        else if (t === 'can-be-commander') c.commander = true;
      } else if (RARITIES.indexOf(t) > -1) c.rarity = t;
      else if (t === 'borderless') c.borderless = true;
      else if (LANGUAGES.indexOf(t) > -1) c.lang = t;
      else if (game !== 'mtg' && isRarity(t, game)) c.rarity = t;
    });
    // v: [id, finish, cents, quantity, condition]; the condition is there only when the product has a Condition option.
    c.foilKind = r.f || '';
    // A special foil reads by its own name ("Ripple foil", not "Foil"); the filters still go by the key.
    c.variants = r.v.map(function (v) { var key = slug(v[1]); return { id: v[0], finish: key === 'foil' && c.foilKind ? c.foilKind : v[1], key: key, price: v[2], qty: v[3], cond: v[4] || '', condKey: v[4] ? slug(v[4]) : '' }; });
    c.qty = 0; c.price = null;
    c.variants.forEach(function (v) { c.qty += v.qty; if (v.qty > 0 && (c.price === null || v.price < c.price)) c.price = v.price; });
    if (c.price === null) c.price = Math.min.apply(null, c.variants.map(function (v) { return v.price; }));
    c.foil = c.variants.every(function (v) { return v.key !== 'nonfoil'; });
    c.typeLine = (c.legendary ? 'Legendary ' : '') + c.types.map(words).join(' ') + (c.sub.length ? ' — ' + c.sub.map(words).join(' ') : '');
    c.search = plain([c.name, c.set, c.code, c.typeLine, c.keywords.join(' '), c.rarity].join(' ').replace(/-/g, ' '));
    c.sortName = plain(c.name);
    return c;
  }

  function fetchPage(n) {
    var url = COLLECTION + (COLLECTION.indexOf('?') > -1 ? '&' : '?') + 'view=wgt-singles-data&page=' + n;
    return fetch(url, { credentials: 'same-origin' }).then(function (r) { if (!r.ok) throw new Error('Catalog ' + r.status); return r.text(); }).then(JSON.parse);
  }

  function loadRows() {
    try {
      var cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (cached && Date.now() - cached.at < CACHE_MS) return Promise.resolve(cached.rows);
    } catch (e) { /* no storage: fetch */ }
    return fetchPage(1).then(function (first) {
      var rest = [];
      for (var n = 2; n <= first.pages; n++) rest.push(fetchPage(n));
      return Promise.all(rest).then(function (parts) {
        var rows = first.cards;
        parts.forEach(function (p) { rows = rows.concat(p.cards); });
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), rows: rows })); } catch (e) { /* too big or blocked: fine */ }
        return rows;
      });
    });
  }

  function load() {
    if (catalogPromise) return catalogPromise;
    var cart = fetch(CART_URL + '.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (c) {
      (c.items || []).forEach(function (i) { inCart[i.variant_id] = (inCart[i.variant_id] || 0) + i.quantity; });
    }).catch(function () {});
    catalogPromise = Promise.all([loadRows(), cart]).then(function (res) {
      cards = res[0].map(parse);
      cards.forEach(function (c) { byHandle[c.handle] = c; setNames[c.setKey] = c.set; });
      return cards;
    });
    return catalogPromise;
  }

  /* ---------- filters ---------- */
  function blankState() { return { q: '', color: [], cmode: 'any', type: [], rarity: [], legal: [], set: [], finish: [], mv: [], does: [], flag: [], condition: [], lang: [], price: '', stock: 'in', sort: 'featured', view: 'grid' }; }

  function readState(search) {
    var p = new URLSearchParams(search || ''), s = blankState();
    s.q = p.get('q') || '';
    LIST_KEYS.forEach(function (k) { var v = p.get(k); s[k] = v ? v.toLowerCase().split(',').filter(Boolean) : []; });
    if (p.get('cmode') === 'id') s.cmode = 'id';
    s.price = p.get('price') || '';
    if (p.get('stock') === 'all') s.stock = 'all';
    if (SORTS.some(function (o) { return o[0] === p.get('sort'); })) s.sort = p.get('sort');
    if (p.get('show') === 'list') s.view = 'list';
    return s;
  }

  function writeState(s, base) {
    var p = new URLSearchParams(base || '');
    ['q', 'cmode', 'price', 'stock', 'sort', 'show'].concat(LIST_KEYS).forEach(function (k) { p.delete(k); });
    if (s.q) p.set('q', s.q);
    LIST_KEYS.forEach(function (k) { if (s[k].length) p.set(k, s[k].join(',')); });
    if (s.cmode === 'id' && s.color.length) p.set('cmode', 'id');
    if (s.price) p.set('price', s.price);
    if (s.stock === 'all') p.set('stock', 'all');
    if (s.sort !== 'featured') p.set('sort', s.sort);
    if (s.view === 'list') p.set('show', 'list');
    return p.toString().replace(/%2C/g, ',');
  }

  function has(c, key, val) {
    switch (key) {
      case 'type': return c.types.indexOf(val) > -1;
      case 'rarity': return c.rarity === val;
      case 'legal': return c.legal.indexOf(val) > -1;
      case 'set': return c.setKey === val;
      case 'finish': return c.variants.some(function (v) { return v.key === val; });
      case 'condition': return c.variants.some(function (v) { return v.condKey === val; });
      case 'lang': return c.lang === val;
      case 'mv': return c.mv !== null && (val === '7' ? c.mv >= 7 : c.mv === parseInt(val, 10));
      case 'does': return c.does.indexOf(val) > -1;
      case 'flag': return val === 'commander' ? c.commander : val === 'legendary' ? c.legendary : val === 'staple' ? c.edh > 0 : val === 'borderless' ? c.borderless : false;
      case 'color': return val === 'c' ? c.colors.length === 0 : val === 'm' ? c.colors.length > 1 : c.colors.indexOf(val) > -1;
    }
    return false;
  }

  function matches(c, s, skip) {
    var i, k;
    if (s.stock !== 'all' && c.qty < 1) return false;
    if (s.q) {
      var toks = plain(s.q).split(/\s+/);
      for (i = 0; i < toks.length; i++) if (toks[i] && c.search.indexOf(toks[i]) < 0) return false;
    }
    if (skip !== 'color' && s.color.length) {
      if (s.cmode === 'id') {
        // Fits a commander of these colors: every color in the card's identity is one you picked.
        for (i = 0; i < c.identity.length; i++) if (s.color.indexOf(c.identity[i]) < 0) return false;
      } else if (!s.color.some(function (v) { return has(c, 'color', v); })) return false;
    }
    for (k = 1; k < LIST_KEYS.length; k++) {
      var key = LIST_KEYS[k], vals = s[key];
      if (key === skip || !vals.length) continue;
      if (ALL_OF[key]) { for (i = 0; i < vals.length; i++) if (!has(c, key, vals[i])) return false; }
      else if (!vals.some(function (v) { return has(c, key, v); })) return false;
    }
    if (skip !== 'price' && s.price) {
      var parts = s.price.split('-'), lo = Math.round(parseFloat(parts[0] || '0') * 100), hi = parts[1] ? Math.round(parseFloat(parts[1]) * 100) : Infinity;
      if (!(c.price >= lo && c.price < hi)) return false;
    }
    return true;
  }

  var SORTERS = {
    'featured': function (a, b) { return (b.edh ? 1 / b.edh : 0) - (a.edh ? 1 / a.edh : 0) || b.price - a.price || a.index - b.index; },
    'price-asc': function (a, b) { return a.price - b.price || (a.sortName < b.sortName ? -1 : 1); },
    'price-desc': function (a, b) { return b.price - a.price || (a.sortName < b.sortName ? -1 : 1); },
    'name': function (a, b) { return a.sortName < b.sortName ? -1 : a.sortName > b.sortName ? 1 : 0; },
    'newest': function (a, b) { return b.created - a.created || a.index - b.index; },
    'mv': function (a, b) { return (a.mv === null ? 99 : a.mv) - (b.mv === null ? 99 : b.mv) || (a.sortName < b.sortName ? -1 : 1); }
  };

  function query(s) {
    if (typeof s === 'string') s = readState(s);
    return cards.filter(function (c) { return matches(c, s); }).sort(SORTERS[s.sort] || SORTERS.featured);
  }

  function browseUrl(s) { var q = writeState(s); return COLLECTION + (q ? '?' + q : ''); }

  /* ---------- a card tile ---------- */
  function pick(c) { // the variant a tile sells: cheapest one in stock
    var best = null;
    c.variants.forEach(function (v) { if (v.qty > 0 && (!best || v.price < best.price)) best = v; });
    return best || c.variants[0];
  }
  function left(v) { return Math.max(0, v.qty - (inCart[v.id] || 0)); }

  function addButton(v, label) {
    if (v.qty < 1) return '<button class="ws-add" type="button" disabled>Sold out</button>';
    if (left(v) < 1) return '<button class="ws-add is-done" type="button" disabled>In cart</button>';
    return '<button class="ws-add" type="button" data-ws-add="' + v.id + '">' + (label || 'Add') + '</button>';
  }

  function picture(c, sizes, eager) {
    if (!c.img) return '';
    return '<img src="' + esc(img(c.img, 488)) + '" srcset="' + esc(img(c.img, 244)) + ' 244w, ' + esc(img(c.img, 488)) + ' 488w, ' + esc(img(c.img, 672)) + ' 672w" sizes="' + sizes + '" width="488" height="680" alt="' + esc(c.name) + '"' + (eager ? '' : ' loading="lazy"') + '>';
  }

  function rar(c, titled) { // the rarity diamond; other games' rarities borrow Magic's colors (common, uncommon, else rare)
    if (!c.rarity) return '';
    var cls = c.game === 'mtg' || RARITIES.indexOf(c.rarity) > -1 ? c.rarity : 'rare';
    return '<i class="ws-rar ws-rar--' + cls + '"' + (titled ? ' title="' + words(c.rarity) + '"' : '') + '></i>';
  }
  // The shine each finish gets (wgt-singles.css .fx-*), as on the card show kiosk.
  var FX = {
    'Ripple foil': 'ripple', 'Surge foil': 'surge', 'Galaxy foil': 'galaxy', 'Cosmic foil': 'galaxy', 'Mana foil': 'galaxy', 'Singularity foil': 'galaxy',
    'Halo foil': 'halo', 'Textured foil': 'textured', 'Raised foil': 'raised', 'Embossed foil': 'raised', 'Confetti foil': 'confetti',
    'Gilded foil': 'gilded', 'Silver foil': 'silver', 'Oil slick foil': 'oilslick', 'Step-and-compleat foil': 'oilslick', 'Neon ink foil': 'neon',
    'Double rainbow foil': 'rainbow2', 'Rainbow foil': 'rainbow2', 'Serialized foil': 'rainbow2', 'Fracture foil': 'fracture',
    'Dragonscale foil': 'dragonscale', 'Invisible ink foil': 'invisible'
  };
  // Pokémon holo rares shine on the picture only; other holofoils (ex, illustration rares) all over.
  var HOLO_WINDOW = ['rare-holo', 'holo-rare', 'rare', 'promo'];
  function fxOf(c, v) {
    if (v.key === 'foil') return FX[c.foilKind] || '';
    if (v.key === 'etched') return 'etched';
    if (v.key === 'holofoil') return HOLO_WINDOW.indexOf(c.rarity) > -1 ? 'holo-window' : 'holo';
    if (v.key === 'reverse-holofoil') return 'reverse';
    return '';
  }
  /** The shine over a card's picture: one version's, or (no version given) the showiest of them. */
  function sheen(c, v) {
    var fx = '';
    (v ? [v] : c.variants).forEach(function (x) { var f = fxOf(c, x); if (f && (!fx || fx === 'reverse' || fx === 'etched')) fx = f; });
    return '<span class="ws-card__sheen' + (fx ? ' fx-' + fx : '') + '" aria-hidden="true"></span>';
  }
  var GRADES = '/pages/card-conditions'; // what NM, LP and the rest mean; linked only where grades show
  function vname(v) { return v.cond ? v.finish + ', ' + v.cond : v.finish; } // "Holofoil", or "Holofoil, Lightly Played" when there is a Condition option

  function tile(c) {
    var v = pick(c), multi = c.variants.length > 1;
    var chips = multi ? '<div class="ws-fins">' + c.variants.map(function (x) {
      return '<button type="button" class="ws-fin ws-fin--pick' + (x === v ? ' is-on' : '') + '" data-ws-fin="' + x.id + '"' + (x.qty < 1 ? ' disabled' : '') + '>' + esc(vname(x)) + '</button>';
    }).join('') + '</div>' : '';
    return '<article class="ws-card' + (v.key !== 'nonfoil' ? ' is-foil' : '') + (c.qty < 1 ? ' is-out' : '') + '" data-ws-card="' + esc(c.handle) + '">' +
      '<a class="ws-card__img" href="' + esc(c.url) + '" data-ws-look="' + esc(c.handle) + '" aria-label="' + esc(c.name) + ', quick look">' + picture(c, '(max-width: 520px) 45vw, 220px') + sheen(c, v) + '</a>' +
      '<div class="ws-card__body">' +
        '<a class="ws-card__name" href="' + esc(c.url) + '">' + esc(c.name) + '</a>' +
        '<p class="ws-card__set">' + rar(c, true) + esc(c.set) + (c.num ? ' · #' + esc(c.num) : '') + '</p>' +
        chips +
        '<div class="ws-card__buy"><span class="ws-card__price" data-ws-price>' + money(v.price) + '</span>' +
          (!multi && (v.key !== 'nonfoil' || v.cond) ? '<span class="ws-fin">' + esc(vname(v)) + '</span>' : '') +
          '<span data-ws-button>' + addButton(v) + '</span></div>' +
      '</div></article>';
  }

  function row(c) {
    var v = pick(c);
    return '<article class="ws-row' + (c.qty < 1 ? ' is-out' : '') + '" data-ws-card="' + esc(c.handle) + '">' +
      '<a class="ws-row__img" href="' + esc(c.url) + '" data-ws-look="' + esc(c.handle) + '" tabindex="-1" aria-hidden="true">' + (c.img ? '<img src="' + esc(img(c.img, 122)) + '" width="61" height="85" alt="" loading="lazy">' : '') + '</a>' +
      '<div class="ws-row__main"><a class="ws-card__name" href="' + esc(c.url) + '">' + esc(c.name) + '</a>' +
        '<p class="ws-card__set">' + rar(c, true) + esc(c.set) + (c.num ? ' · #' + esc(c.num) : '') + '</p></div>' +
      (c.game === 'mtg' ? '<p class="ws-row__type">' + pips(c.colors.length ? c.colors : ['c']) + '<span>' + esc(c.typeLine) + '</span></p>' : '<p class="ws-row__type"><span>' + (c.rarity ? words(c.rarity) : '') + '</span></p>') +
      '<p class="ws-row__fin">' + (v.key !== 'nonfoil' ? '<span class="ws-fin">' + esc(vname(v)) + '</span>' : esc(vname(v))) + '</p>' +
      '<p class="ws-row__qty">' + (c.qty < 1 ? 'Sold out' : c.qty > 20 ? 'In stock' : c.qty + ' left') + '</p>' +
      '<p class="ws-card__price" data-ws-price>' + money(v.price) + '</p>' +
      '<span data-ws-button>' + addButton(v) + '</span></article>';
  }

  function pips(list) {
    return '<span class="ws-pips">' + list.map(function (k) { return '<i class="ws-pip ws-pip--' + k + ' ms ms-' + (k === 'm' ? 'multicolor' : k) + '" title="' + COLOR_NAME[k] + '"></i>'; }).join('') + '</span>';
  }

  /* ---------- cart ---------- */
  var toastEl, toastTimer;
  function toast(html, bad) {
    if (!toastEl) { toastEl = document.createElement('div'); toastEl.className = 'ws-toast'; toastEl.setAttribute('role', 'status'); toastEl.setAttribute('aria-live', 'polite'); }
    var host = look && look.open ? look : document.body; // an open dialog covers the page, so the toast goes inside it
    if (toastEl.parentNode !== host) host.appendChild(toastEl);
    toastEl.innerHTML = html;
    toastEl.classList.toggle('is-bad', !!bad);
    toastEl.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.classList.remove('is-on'); }, bad ? 6000 : 4200);
  }

  function refreshThemeCart() { // keeps the theme's cart drawer and header count in step
    var form = document.getElementById('AjaxCartForm');
    if (!form) return Promise.resolve();
    return fetch(window.location.pathname + '?section_id=helper-cart', { credentials: 'same-origin' }).then(function (r) { return r.text(); }).then(function (t) {
      var doc = new DOMParser().parseFromString(t, 'text/html'), fresh = doc.getElementById('AjaxCartForm'), sub = doc.getElementById('AjaxCartSubtotal');
      if (!fresh) return;
      form.innerHTML = fresh.innerHTML;
      if (typeof form.ajaxifyCartItems === 'function') form.ajaxifyCartItems();
      if (sub && document.getElementById('AjaxCartSubtotal')) document.getElementById('AjaxCartSubtotal').innerHTML = sub.innerHTML;
      var count = form.querySelector('[data-cart-count]'), total = form.querySelector('[data-cart-total]');
      if (count) $$('[data-header-cart-count]').forEach(function (el) { el.textContent = count.textContent; });
      if (total) $$('[data-header-cart-total]').forEach(function (el) { el.textContent = total.textContent; });
    }).catch(function () {});
  }

  function addOne(variantId) {
    return fetch(CART_ADD, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }) })
      .then(function (r) { return r.json().then(function (j) { if (!r.ok) throw new Error(j.description || j.message || 'That one could not be added.'); return j; }); })
      .then(function (j) { inCart[variantId] = (inCart[variantId] || 0) + 1; return j; });
  }

  function variantOf(id) {
    for (var i = 0; i < cards.length; i++) for (var j = 0; j < cards[i].variants.length; j++) if (cards[i].variants[j].id === id) return { card: cards[i], variant: cards[i].variants[j] };
    return null;
  }

  function redrawButtons(variantId) {
    var hit = variantOf(variantId);
    if (!hit) return;
    $$('[data-ws-add="' + variantId + '"]').forEach(function (b) { if (b.parentElement && b.parentElement.hasAttribute('data-ws-button')) b.parentElement.innerHTML = addButton(hit.variant, b.getAttribute('data-label')); else b.outerHTML = addButton(hit.variant, b.getAttribute('data-label')); });
  }

  function cartLink() { return '<button type="button" class="ws-toast__link" data-ws-cart>View cart</button>'; }

  function onAdd(btn) {
    var id = parseInt(btn.getAttribute('data-ws-add'), 10), hit = variantOf(id);
    btn.disabled = true; btn.classList.add('is-busy');
    addOne(id).then(function () {
      toast('<span>Added <b>' + esc(hit ? hit.card.name : 'card') + '</b></span>' + cartLink());
      redrawButtons(id);
      $$('.ws-add.is-busy').forEach(function (b) { b.disabled = false; b.classList.remove('is-busy'); });
      refreshThemeCart(); drawStoryTotals();
    }).catch(function (e) {
      btn.disabled = false; btn.classList.remove('is-busy');
      toast('<span>' + esc(e.message) + '</span>', true);
    });
  }

  function addMany(ids, btn) {
    var done = 0, failed = 0, i = 0;
    btn.disabled = true; btn.classList.add('is-busy');
    (function next() {
      if (i >= ids.length) {
        btn.classList.remove('is-busy');
        toast('<span>Added <b>' + done + ' card' + (done === 1 ? '' : 's') + '</b>' + (failed ? ' (' + failed + ' just sold out)' : '') + '</span>' + cartLink(), !done);
        refreshThemeCart(); drawStoryTotals();
        return;
      }
      var id = ids[i++];
      addOne(id).then(function () { done++; redrawButtons(id); }, function () { failed++; }).then(next);
    })();
  }

  /* ---------- quick look ---------- */
  var look;
  function openLook(c) {
    if (!look) {
      look = document.createElement('dialog');
      look.className = 'ws-look';
      document.body.appendChild(look);
      look.addEventListener('click', function (e) { if (e.target === look || e.target.closest('[data-ws-close]')) look.close(); });
    }
    var filters = [];
    c.does.forEach(function (d) { filters.push('<a class="ws-tag" href="' + esc(COLLECTION + '?does=' + d) + '">' + esc(DOES[d] || words(d)) + '</a>'); });
    var s = blankState(); s.set = [c.setKey];
    look.innerHTML =
      '<button class="ws-look__close" type="button" data-ws-close aria-label="Close">×</button>' +
      '<div class="ws-look__in">' +
        '<div class="ws-look__art' + (c.foil ? ' is-foil' : '') + '">' + picture(c, '(max-width: 760px) 70vw, 380px', true) + sheen(c) + '<span class="ws-look__glare" aria-hidden="true"></span></div>' +
        '<div class="ws-look__body">' +
          '<p class="ws-look__set">' + (c.rarity ? rar(c) + words(c.rarity) + ' · ' : '') + '<a href="' + esc(browseUrl(s)) + '">' + esc(c.set) + '</a>' + (c.num ? ' #' + esc(c.num) : '') + '</p>' +
          '<h2>' + esc(c.name) + '</h2>' +
          (c.game === 'mtg' ? '<p class="ws-look__type">' + pips(c.colors.length ? c.colors : ['c']) + esc(c.typeLine) + (c.mv !== null && c.types.indexOf('land') < 0 ? ' · mana value ' + c.mv : '') + '</p>' : '') +
          '<div class="ws-look__buy">' + c.variants.map(function (v) {
            return '<div class="ws-look__variant"><span><b>' + money(v.price) + '</b> ' + esc(vname(v)) + '</span><span class="ws-look__qty">' + (v.qty < 1 ? '' : v.qty > 20 ? 'In stock' : v.qty === 1 ? 'Last one' : v.qty + ' left') + '</span><span data-ws-button>' + addButton(v, 'Add to cart') + '</span></div>';
          }).join('') + '</div>' +
          (c.game === 'mtg' ? '<div class="ws-look__text" data-ws-text><p class="ws-look__wait">Loading the card text…</p></div>' : '') +
          (c.legal.length ? '<p class="ws-look__legal">' + FORMATS.filter(function (f) { return c.legal.indexOf(f) > -1; }).map(function (f) { return '<span>' + words(f) + '</span>'; }).join('') + '</p>' : '') +
          (filters.length ? '<p class="ws-look__more">More like it: ' + filters.join('') + '</p>' : '') +
          '<p class="ws-look__links"><a class="ws-look__full" href="' + esc(c.url) + '">Full details</a>' + (c.variants.some(function (v) { return v.cond; }) ? '<a href="' + GRADES + '" target="_blank">How we grade cards</a>' : '') + '</p>' +
        '</div></div>';
    if (typeof look.showModal === 'function') look.showModal(); else { window.location.href = c.url; return; }
    if (c.game !== 'mtg') return; // the rules text is the description's 2nd paragraph only in Magic listings
    fetch(c.url + '.js').then(function (r) { return r.json(); }).then(function (p) {
      var box = $('[data-ws-text]', look); if (!box || !look.open) return;
      var doc = new DOMParser().parseFromString(p.description || '', 'text/html'), ps = doc.body.querySelectorAll('p');
      // The listing's 2nd paragraph is the card's rules text; an italic one after it is flavor text.
      var out = '';
      if (ps[1]) out += '<p>' + esc(ps[1].textContent) + '</p>';
      if (ps[2] && ps[2].querySelector('em') && ps.length > 3) out += '<p><em>' + esc(ps[2].textContent) + '</em></p>';
      box.innerHTML = out;
    }).catch(function () { var box = $('[data-ws-text]', look); if (box) box.innerHTML = ''; });
  }

  /* ---------- the quick look's card tilts toward the pointer ---------- */
  function tilt(e) {
    var art = e.target.closest && e.target.closest('.ws-look__art');
    if (!art) return;
    var box = art.getBoundingClientRect(), x = Math.max(0, Math.min(1, (e.clientX - box.left) / box.width)), y = Math.max(0, Math.min(1, (e.clientY - box.top) / box.height));
    art.classList.add('is-live');
    art.style.setProperty('--rx', ((.5 - y) * 16).toFixed(2) + 'deg');
    art.style.setProperty('--ry', ((x - .5) * 20).toFixed(2) + 'deg');
    art.style.setProperty('--mx', (x * 100).toFixed(1) + '%');
    art.style.setProperty('--my', (y * 100).toFixed(1) + '%');
  }
  function untilt(e) {
    var art = e.target.closest && e.target.closest('.ws-look__art');
    if (!art || (e.type === 'pointerout' && art.contains(e.relatedTarget))) return;
    art.classList.remove('is-live');
    art.style.setProperty('--rx', '0deg'); art.style.setProperty('--ry', '0deg');
  }
  if (!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
    document.addEventListener('pointermove', tilt, { passive: true });
    document.addEventListener('pointerdown', tilt, { passive: true });
    ['pointerout', 'pointerup', 'pointercancel'].forEach(function (ev) { document.addEventListener(ev, untilt, { passive: true }); });
  }

  /* ---------- page-wide clicks ---------- */
  document.addEventListener('click', function (e) {
    var t = e.target, el;
    if ((el = t.closest('[data-ws-add]'))) { e.preventDefault(); onAdd(el); return; }
    if ((el = t.closest('[data-ws-cart]'))) {
      var drawer = document.getElementById('site-cart-sidebar');
      if (look && look.open) look.close();
      if (drawer && typeof drawer.show === 'function') drawer.show(); else window.location.href = CART_URL;
      return;
    }
    if ((el = t.closest('[data-ws-fin]'))) {
      var hit = variantOf(parseInt(el.getAttribute('data-ws-fin'), 10)), card = el.closest('[data-ws-card]');
      if (!hit || !card) return;
      $$('[data-ws-fin]', card).forEach(function (b) { b.classList.toggle('is-on', b === el); });
      $('[data-ws-price]', card).textContent = money(hit.variant.price);
      $('[data-ws-button]', card).innerHTML = addButton(hit.variant);
      card.classList.toggle('is-foil', hit.variant.key !== 'nonfoil');
      var shine = $('.ws-card__sheen', card);
      if (shine) shine.outerHTML = sheen(hit.card, hit.variant);
      return;
    }
    if ((el = t.closest('[data-ws-look]'))) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button) return; // new tab: let the link work
      var c = byHandle[el.getAttribute('data-ws-look')];
      if (c) { e.preventDefault(); openLook(c); }
    }
  });

  /* ---------- the browse page ---------- */
  function initBrowser(root) {
    var state = readState(window.location.search), shown = PAGE, results = [];
    var grid = $('[data-ws-grid]', root), panel = $('[data-ws-filters]', root), chipsEl = $('[data-ws-chips]', root), countEl = $('[data-ws-count]', root);
    var moreEl = $('[data-ws-more]', root), qEl = $('[data-ws-q]', root), sortEl = $('[data-ws-sort]', root), badge = $('[data-ws-badge]', root);
    var open = { color: true, type: true, price: true, rarity: true }, setSearch = '', showAllDoes = false;

    sortEl.innerHTML = SORTS.filter(function (o) { return GAME === 'mtg' || o[0] !== 'mv'; }).map(function (o) { return '<option value="' + o[0] + '">' + o[1] + '</option>'; }).join('');

    function label(key, val) {
      if (key === 'color') return COLOR_NAME[val] || val;
      if (key === 'set') return setNames[val] || words(val);
      if (key === 'does') return DOES[val] || words(val);
      if (key === 'mv') return 'Mana value ' + (val === '7' ? '7+' : val);
      if (key === 'legal') return words(val) + ' legal';
      if (key === 'flag') { var f = FLAGS.filter(function (o) { return o[0] === val; })[0]; return f ? f[1] : val; }
      return words(val);
    }

    function counts(key, pool) {
      var n = {};
      pool.forEach(function (c) {
        if (key === 'set') n[c.setKey] = (n[c.setKey] || 0) + 1;
        else if (key === 'does') c.does.forEach(function (d) { n[d] = (n[d] || 0) + 1; });
        else if (key === 'type') c.types.forEach(function (d) { n[d] = (n[d] || 0) + 1; });
        else if (key === 'legal') c.legal.forEach(function (d) { n[d] = (n[d] || 0) + 1; });
        else if (key === 'rarity') n[c.rarity] = (n[c.rarity] || 0) + 1;
        else if (key === 'lang') n[c.lang] = (n[c.lang] || 0) + 1;
        else if (key === 'finish') c.variants.forEach(function (v) { if (v.qty > 0 || state.stock === 'all') n[v.key] = (n[v.key] || 0) + 1; });
        else if (key === 'condition') { var seen = {}; c.variants.forEach(function (v) { if (v.condKey && !seen[v.condKey] && (v.qty > 0 || state.stock === 'all')) { seen[v.condKey] = 1; n[v.condKey] = (n[v.condKey] || 0) + 1; } }); }
        else if (key === 'mv') { if (c.mv !== null) { var k = c.mv >= 7 ? '7' : String(c.mv); n[k] = (n[k] || 0) + 1; } }
        else if (key === 'flag') FLAGS.forEach(function (o) { if (has(c, 'flag', o[0])) n[o[0]] = (n[o[0]] || 0) + 1; });
        else if (key === 'color') ['w', 'u', 'b', 'r', 'g', 'c', 'm'].forEach(function (k) { if (has(c, 'color', k)) n[k] = (n[k] || 0) + 1; });
      });
      return n;
    }
    // For "any of" groups the count ignores the group's own picks (what you'd get by adding it);
    // for "all of" groups it is what would be left.
    function pool(key) { return cards.filter(function (c) { return matches(c, state, ALL_OF[key] ? null : key); }); }

    function group(key, title, body, extra) {
      var picked = key === 'price' ? (state.price ? 1 : 0) : state[key].length;
      return '<details class="ws-group" data-ws-group="' + key + '"' + (open[key] || picked ? ' open' : '') + '><summary>' + title + (picked ? '<b>' + picked + '</b>' : '') + '</summary><div class="ws-group__body">' + body + (extra || '') + '</div></details>';
    }
    function checks(key, options, n, cls) {
      return '<ul class="ws-checks' + (cls ? ' ' + cls : '') + '">' + options.map(function (o) {
        var on = state[key].indexOf(o[0]) > -1, count = n[o[0]] || 0;
        if (!count && !on) return '';
        return '<li data-ws-name="' + esc(plain(o[1])) + '"><label><input type="checkbox" data-ws-key="' + key + '" value="' + esc(o[0]) + '"' + (on ? ' checked' : '') + '><span>' + (o[2] || '') + esc(o[1]) + '</span><em>' + count + '</em></label></li>';
      }).join('') + '</ul>';
    }

    function drawPanel() {
      var focus = document.activeElement && panel.contains(document.activeElement) ? document.activeElement : null;
      var fkey = focus && focus.getAttribute('data-ws-key'), fval = focus && focus.value;
      var html = '';

      if (GAME === 'mtg') {
      var cn = counts('color', pool('color'));
      html += group('color', 'Color',
        '<div class="ws-colors">' + ['w', 'u', 'b', 'r', 'g', 'c', 'm'].map(function (k) {
          if (state.cmode === 'id' && (k === 'c' || k === 'm')) return '';
          var on = state.color.indexOf(k) > -1;
          return '<label class="ws-color' + (on ? ' is-on' : '') + '" title="' + COLOR_NAME[k] + (state.cmode === 'id' ? '' : ' (' + (cn[k] || 0) + ')') + '"><input type="checkbox" data-ws-key="color" value="' + k + '"' + (on ? ' checked' : '') + '><i class="ws-pip ws-pip--' + k + ' ms ms-' + (k === 'm' ? 'multicolor' : k) + '"></i><span class="ws-sr">' + COLOR_NAME[k] + '</span></label>';
        }).join('') + '</div>' +
        '<label class="ws-switch"><input type="checkbox" data-ws-cmode' + (state.cmode === 'id' ? ' checked' : '') + '><span>Fits my commander’s colors</span></label>' +
        '<p class="ws-hint">' + (state.cmode === 'id' ? 'Pick your commander’s colors. You’ll see every card that deck can play, colorless included.' : 'Shows cards with any color you pick.') + '</p>');

      html += group('type', 'Card type', checks('type', TYPES.map(function (t) { return [t, words(t)]; }), counts('type', pool('type'))));
      var pn = {}; pool('price').forEach(function (c) { PRICES.forEach(function (o) { var p = o[0].split('-'), lo = parseFloat(p[0]) * 100, hi = p[1] ? parseFloat(p[1]) * 100 : Infinity; if (c.price >= lo && c.price < hi) pn[o[0]] = (pn[o[0]] || 0) + 1; }); });
      html += group('price', 'Price', '<ul class="ws-checks">' + PRICES.map(function (o) {
        var on = state.price === o[0]; if (!pn[o[0]] && !on) return '';
        return '<li><label><input type="checkbox" data-ws-price-pick value="' + o[0] + '"' + (on ? ' checked' : '') + '><span>' + o[1] + '</span><em>' + (pn[o[0]] || 0) + '</em></label></li>';
      }).join('') + '</ul>');
      html += group('rarity', 'Rarity', checks('rarity', RARITIES.map(function (r) { return [r, r === 'mythic' ? 'Mythic rare' : words(r), '<i class="ws-rar ws-rar--' + r + '"></i>']; }), counts('rarity', pool('rarity'))));
      html += group('legal', 'Legal in', checks('legal', FORMATS.map(function (f) { return [f, words(f)]; }), counts('legal', pool('legal'))));

      var mn = counts('mv', pool('mv'));
      html += group('mv', 'Mana value', '<div class="ws-mvs">' + ['0', '1', '2', '3', '4', '5', '6', '7'].map(function (k) {
        var on = state.mv.indexOf(k) > -1;
        return '<label class="ws-mv' + (on ? ' is-on' : '') + (!mn[k] && !on ? ' is-empty' : '') + '"><input type="checkbox" data-ws-key="mv" value="' + k + '"' + (on ? ' checked' : '') + (!mn[k] && !on ? ' disabled' : '') + '><span>' + (k === '7' ? '7+' : k) + '</span></label>';
      }).join('') + '</div>');

      var dn = counts('does', pool('does')), dopts = Object.keys(dn).concat(state.does.filter(function (d) { return !dn[d]; })).sort(function (a, b) { return (dn[b] || 0) - (dn[a] || 0); }).map(function (d) { return [d, DOES[d] || words(d)]; });
      var few = showAllDoes ? dopts : dopts.filter(function (o, i) { return i < 10 || state.does.indexOf(o[0]) > -1; });
      html += group('does', 'What it does', checks('does', few, dn), dopts.length > few.length ? '<button type="button" class="ws-linkbtn" data-ws-all-does>Show all ' + dopts.length + '</button>' : '');

      html += group('flag', 'For Commander', checks('flag', FLAGS, counts('flag', pool('flag'))));
      html += group('finish', 'Finish', checks('finish', [['nonfoil', 'Nonfoil'], ['foil', 'Foil'], ['etched', 'Etched foil']], counts('finish', pool('finish'))));
      } else html += otherFilters();
      html += conditionFilter();
      html += languageFilter();

      var sn = counts('set', pool('set')), sopts = Object.keys(sn).concat(state.set.filter(function (k) { return !sn[k]; })).sort(function (a, b) { return (sn[b] || 0) - (sn[a] || 0) || (a < b ? -1 : 1); }).map(function (k) { return [k, setNames[k] || words(k)]; });
      html += group('set', 'Set', '<input class="ws-setsearch" type="search" placeholder="Find a set" aria-label="Find a set" data-ws-setsearch value="' + esc(setSearch) + '">' + checks('set', sopts, sn, 'ws-checks--scroll'));

      html += '<label class="ws-switch ws-switch--foot"><input type="checkbox" data-ws-stock' + (state.stock === 'all' ? ' checked' : '') + '><span>Show sold-out cards too</span></label>';
      panel.innerHTML = html;
      filterSets();
      if (fkey) { var again = panel.querySelector('[data-ws-key="' + fkey + '"][value="' + fval + '"]'); if (again) again.focus(); }
    }

    // Every game but Magic: price, rarity and finish (sets and sold-out cards follow, as for Magic). Their listings
    // carry no color, type or format tags, so those filters aren't offered.
    function otherFilters() {
      var html = '', pn = {};
      pool('price').forEach(function (c) { PRICES.forEach(function (o) { var p = o[0].split('-'), lo = parseFloat(p[0]) * 100, hi = p[1] ? parseFloat(p[1]) * 100 : Infinity; if (c.price >= lo && c.price < hi) pn[o[0]] = (pn[o[0]] || 0) + 1; }); });
      html += group('price', 'Price', '<ul class="ws-checks">' + PRICES.map(function (o) {
        var on = state.price === o[0]; if (!pn[o[0]] && !on) return '';
        return '<li><label><input type="checkbox" data-ws-price-pick value="' + o[0] + '"' + (on ? ' checked' : '') + '><span>' + o[1] + '</span><em>' + (pn[o[0]] || 0) + '</em></label></li>';
      }).join('') + '</ul>');
      var order = GAMES[GAME].rarities || [], rn = counts('rarity', pool('rarity'));
      var ropts = Object.keys(rn).concat(state.rarity.filter(function (r) { return !rn[r]; })).filter(Boolean).sort(function (a, b) {
        var i = order.indexOf(a), j = order.indexOf(b);
        return (i < 0 ? 99 : i) - (j < 0 ? 99 : j) || (a < b ? -1 : 1);
      }).map(function (r) { return [r, words(r), rar({ game: GAME, rarity: r })]; });
      html += group('rarity', 'Rarity', checks('rarity', ropts, rn));
      var fn = counts('finish', pool('finish')), fopts = GAMES[GAME].finishes.slice();
      Object.keys(fn).forEach(function (k) { if (!fopts.some(function (o) { return o[0] === k; })) fopts.push([k, words(k)]); });
      html += group('finish', 'Finish', checks('finish', fopts, fn));
      return html;
    }

    // Condition, for any game, once listings have a Condition option (Magic's have only Finish so far).
    function conditionFilter() {
      var n = counts('condition', pool('condition'));
      var keys = Object.keys(n).concat(state.condition.filter(function (k) { return !n[k]; }));
      if (!keys.length) return '';
      var names = {};
      cards.forEach(function (c) { c.variants.forEach(function (v) { if (v.condKey) names[v.condKey] = v.cond; }); });
      keys.sort(function (a, b) { var i = CONDITIONS.indexOf(a), j = CONDITIONS.indexOf(b); return (i < 0 ? 99 : i) - (j < 0 ? 99 : j) || (a < b ? -1 : 1); });
      return group('condition', 'Condition', checks('condition', keys.map(function (k) { return [k, names[k] || words(k)]; }), n), '<p class="ws-hint"><a href="' + GRADES + '" target="_blank">What the grades mean</a></p>');
    }

    // Language, for any game, only when the cards come in more than one (English and Japanese, say).
    function languageFilter() {
      var n = counts('lang', pool('lang'));
      var keys = Object.keys(n).concat(state.lang.filter(function (k) { return !n[k]; }));
      if (keys.length < 2 && !state.lang.length) return '';
      keys.sort(function (a, b) { return (a === 'english' ? -1 : b === 'english' ? 1 : 0) || (n[b] || 0) - (n[a] || 0) || (a < b ? -1 : 1); });
      return group('lang', 'Language', checks('lang', keys.map(function (k) { return [k, words(k)]; }), n));
    }

    function filterSets() {
      var term = plain(setSearch);
      $$('[data-ws-group="set"] li', panel).forEach(function (li) { li.hidden = !!term && li.getAttribute('data-ws-name').indexOf(term) < 0; });
    }

    function drawChips() {
      var out = [];
      if (state.q) out.push(['q', '', '“' + state.q + '”']);
      LIST_KEYS.forEach(function (k) { state[k].forEach(function (v) { out.push([k, v, (k === 'color' && state.cmode === 'id' ? 'Identity: ' : '') + label(k, v)]); }); });
      if (state.price) { var p = PRICES.filter(function (o) { return o[0] === state.price; })[0]; out.push(['price', '', p ? p[1] : '$' + state.price.replace('-', ' to $')]); }
      chipsEl.innerHTML = out.map(function (c) { return '<button type="button" class="ws-chip" data-ws-drop="' + c[0] + '" data-val="' + esc(c[1]) + '">' + esc(c[2]) + '<span aria-hidden="true">×</span><span class="ws-sr"> remove</span></button>'; }).join('') +
        (out.length > 1 ? '<button type="button" class="ws-linkbtn" data-ws-clear>Clear all</button>' : '');
      chipsEl.hidden = !out.length;
      if (badge) { badge.textContent = out.length; badge.hidden = !out.length; }
    }

    function drawGrid() {
      var list = results.slice(0, shown);
      grid.className = state.view === 'list' ? 'ws-list' : 'ws-grid';
      if (!results.length) {
        grid.innerHTML = '<div class="ws-empty"><h2>Nothing in the binder matches that.</h2><p>Try fewer filters, or check the spelling of the card name.</p><button type="button" class="ws-btn" data-ws-clear>Clear all filters</button></div>';
      } else grid.innerHTML = list.map(state.view === 'list' ? row : tile).join('');
      moreEl.hidden = shown >= results.length;
      countEl.textContent = results.length === 1 ? '1 card' : results.length.toLocaleString() + ' cards';
      $$('[data-ws-view]', root).forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-ws-view') === state.view ? 'true' : 'false'); });
    }

    function update(keepShown) {
      if (!keepShown) shown = PAGE;
      results = query(state);
      drawPanel(); drawChips(); drawGrid();
      var q = writeState(state, window.location.search);
      history.replaceState(null, '', window.location.pathname + (q ? '?' + q : '') + window.location.hash);
    }

    function toggle(key, val, on) {
      var i = state[key].indexOf(val);
      if (on && i < 0) state[key].push(val);
      if (!on && i > -1) state[key].splice(i, 1);
    }

    function clearAll() { var keep = { sort: state.sort, view: state.view }; state = blankState(); state.sort = keep.sort; state.view = keep.view; qEl.value = ''; setSearch = ''; }

    panel.addEventListener('change', function (e) {
      var t = e.target;
      if (t.hasAttribute('data-ws-key')) toggle(t.getAttribute('data-ws-key'), t.value, t.checked);
      else if (t.hasAttribute('data-ws-price-pick')) state.price = t.checked ? t.value : '';
      else if (t.hasAttribute('data-ws-cmode')) { state.cmode = t.checked ? 'id' : 'any'; if (t.checked) state.color = state.color.filter(function (k) { return WUBRG.indexOf(k) > -1; }); }
      else if (t.hasAttribute('data-ws-stock')) state.stock = t.checked ? 'all' : 'in';
      else return;
      update();
    });
    panel.addEventListener('input', function (e) { if (e.target.hasAttribute('data-ws-setsearch')) { setSearch = e.target.value; filterSets(); } });
    panel.addEventListener('toggle', function (e) { var g = e.target.getAttribute && e.target.getAttribute('data-ws-group'); if (g) open[g] = e.target.open; }, true);
    root.addEventListener('click', function (e) {
      var el;
      if ((el = e.target.closest('[data-ws-drop]'))) { var k = el.getAttribute('data-ws-drop'); if (k === 'q') { state.q = ''; qEl.value = ''; } else if (k === 'price') state.price = ''; else toggle(k, el.getAttribute('data-val'), false); update(); }
      else if (e.target.closest('[data-ws-clear]')) { clearAll(); update(); }
      else if (e.target.closest('[data-ws-all-does]')) { showAllDoes = true; drawPanel(); }
      else if ((el = e.target.closest('[data-ws-view]'))) { state.view = el.getAttribute('data-ws-view'); update(true); }
      else if (e.target.closest('[data-ws-more-btn]')) { shown += PAGE; drawGrid(); }
      else if (e.target.closest('[data-ws-open]')) { root.classList.add('is-filtering'); document.documentElement.classList.add('ws-lock'); var side = $('.wsb-side', root); side.setAttribute('tabindex', '-1'); side.focus(); }
      else if (e.target.closest('[data-ws-shut]')) shut();
      else if ((el = e.target.closest('a[data-ws-shelf]'))) { // a shortcut link to a filtered view of this same page
        e.preventDefault();
        var keep = { sort: state.sort, view: state.view };
        state = readState(el.search); if (state.sort === 'featured') state.sort = keep.sort; state.view = keep.view; qEl.value = state.q;
        update(); sortEl.value = state.sort;
        $('[data-ws-bar]', root).scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
    function shut() { root.classList.remove('is-filtering'); document.documentElement.classList.remove('ws-lock'); }
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && root.classList.contains('is-filtering')) shut(); });

    var typing;
    qEl.addEventListener('input', function () { clearTimeout(typing); typing = setTimeout(function () { state.q = qEl.value.trim(); update(); }, 140); });
    qEl.form && qEl.form.addEventListener('submit', function (e) { e.preventDefault(); qEl.blur(); });
    sortEl.addEventListener('change', function () { state.sort = sortEl.value; update(); });

    // Keep loading as the shopper nears the end of what is shown.
    if ('IntersectionObserver' in window) new IntersectionObserver(function (entries) { if (entries[0].isIntersecting && shown < results.length) { shown += PAGE; drawGrid(); } }, { rootMargin: '900px 0px' }).observe(moreEl);

    // Sit the toolbar under the theme's header when that header is sticky.
    var header = document.querySelector('.site-header'), lastTop = -1;
    function place() {
      var top = 0;
      for (var el = header; el && el !== document.body; el = el.parentElement) { var pos = getComputedStyle(el).position; if (pos === 'sticky' || pos === 'fixed') { top = Math.max(0, Math.round(header.getBoundingClientRect().bottom)); break; } }
      if (top !== lastTop) { root.style.setProperty('--ws-top', top + 'px'); lastTop = top; }
    }
    window.addEventListener('scroll', place, { passive: true }); window.addEventListener('resize', place); place();

    qEl.value = state.q; sortEl.value = state.sort;
    root.classList.add('is-loading');
    // A link that arrives with filters already set (from an article, a shortcut, a shared address) starts at the cards, not the heading.
    var deep = !!(state.q || state.price || LIST_KEYS.some(function (k) { return state[k].length; })) && !window.location.hash;
    function toCards() { place(); $('[data-ws-bar]', root).scrollIntoView({ block: 'start' }); }
    var landed = 0;
    if (deep) { toCards(); landed = window.scrollY; }
    load().then(function () { root.classList.remove('is-loading'); root.classList.add('is-ready'); update(); if (deep && Math.abs(window.scrollY - landed) < 80) toCards(); }) // again once the cards are drawn, unless the shopper has scrolled
      .catch(function () { root.classList.remove('is-loading'); root.classList.add('is-broken'); }); // the server-rendered cards and page links stay
  }

  /* ---------- live cards inside articles ---------- */
  function handleOf(a) { var m = /\/products\/([^/?#]+)/.exec(a.getAttribute('href') || ''); return m ? decodeURIComponent(m[1]) : null; }

  function initRails() {
    $$('.wgt-rail').forEach(function (el) {
      var a = $('a[href]', el); if (!a || el.classList.contains('ws-rail')) return;
      if (el.tagName !== 'DIV') { var box = document.createElement('div'); box.className = el.className; if (el.hasAttribute('data-limit')) box.setAttribute('data-limit', el.getAttribute('data-limit')); el.parentNode.replaceChild(box, el); el = box; }
      var s = readState(a.search), found = query(s), limit = parseInt(el.getAttribute('data-limit'), 10) || 6;
      var url = browseUrl(s), title = a.textContent.trim();
      el.classList.add('ws-rail');
      el.innerHTML = '<div class="ws-rail__head"><h3>' + esc(title) + '</h3>' + (found.length ? '<a class="ws-rail__all" href="' + esc(url) + '">' + (found.length > limit ? 'See all ' + found.length : 'Open in the binder') + ' <span aria-hidden="true">→</span></a>' : '') + '</div>' +
        (found.length ? '<div class="ws-rail__track">' + found.slice(0, limit).map(tile).join('') + '</div>'
          : '<p class="ws-rail__none">These have all sold. <a href="' + esc(COLLECTION) + '">See what’s in the binder now</a>.</p>');
    });
  }

  function initSpots() {
    $$('.wgt-spot').forEach(function (el) {
      var a = $('a[href*="/products/"]', el); if (!a || el.classList.contains('is-ready')) return;
      var c = byHandle[handleOf(a)], v;
      el.classList.add('is-ready');
      if (!c) { el.insertAdjacentHTML('beforeend', '<p class="ws-spot__buy ws-spot__buy--gone">This one has sold. <a href="' + esc(COLLECTION + '?q=' + encodeURIComponent(a.textContent.trim())) + '">Look for another copy</a></p>'); return; }
      v = pick(c);
      var text = document.createElement('div'); text.className = 'ws-spot__text';
      while (el.firstChild) text.appendChild(el.firstChild);
      el.innerHTML = '<a class="ws-spot__art' + (v.key !== 'nonfoil' ? ' is-foil' : '') + '" href="' + esc(c.url) + '" data-ws-look="' + esc(c.handle) + '" aria-label="' + esc(c.name) + ', quick look">' + picture(c, '(max-width: 640px) 46vw, 240px') + sheen(c, v) + '</a>';
      el.appendChild(text);
      text.insertAdjacentHTML('beforeend', '<p class="ws-spot__buy"><span class="ws-card__price">' + money(v.price) + '</span><span class="ws-spot__meta">' + rar(c) + esc(c.set) + (v.key !== 'nonfoil' || v.cond ? ' · ' + esc(vname(v)) : '') + '</span><span data-ws-button>' + addButton(v, 'Add to cart') + '</span></p>');
    });
  }

  var storyBox, storyCards = [];
  function drawStoryTotals() {
    if (!storyBox) return;
    var btn = $('[data-ws-add-all]', storyBox); if (!btn) return;
    var ids = [], sum = 0;
    storyCards.forEach(function (c) { var v = pick(c); if (left(v) > 0) { ids.push(v.id); sum += v.price; } });
    btn.hidden = ids.length < 2;
    btn.textContent = 'Add all ' + ids.length + ' · ' + money(sum);
    btn.disabled = false;
    btn._ids = ids;
  }

  function initArticle(root) {
    var seen = {}, tip;
    $$('a[href*="/products/"]', root).forEach(function (a) {
      var c = byHandle[handleOf(a)]; if (!c || a.closest('.ws-rail')) return; // a rail's cards are not "in the story"
      if (!seen[c.handle]) { seen[c.handle] = true; storyCards.push(c); }
      if (a.closest('.ws-spot__art, .ws-card, .ws-spot__buy')) return;
      a.classList.add('ws-cardlink');
      a.setAttribute('data-ws-tip', c.handle);
    });

    // A floating picture of the card while a card link is hovered or focused (not on touch).
    if (window.matchMedia('(hover: hover)').matches) {
      tip = document.createElement('div'); tip.className = 'ws-tip'; tip.setAttribute('aria-hidden', 'true'); document.body.appendChild(tip);
      var show = function (a) {
        var c = byHandle[a.getAttribute('data-ws-tip')]; if (!c) return;
        var v = pick(c), r = a.getBoundingClientRect(), w = 230, x = Math.min(Math.max(8, r.left + r.width / 2 - w / 2), window.innerWidth - w - 8);
        tip.innerHTML = picture(c, '230px', true) + '<p><b>' + money(v.price) + '</b> ' + (c.qty < 1 ? 'Sold out' : esc(vname(v)) + ' · in stock') + '</p>';
        tip.style.left = x + 'px';
        tip.style.top = (r.top > 380 ? r.top + window.scrollY - 372 : r.bottom + window.scrollY + 10) + 'px';
        tip.classList.add('is-on');
      };
      var hide = function () { tip.classList.remove('is-on'); };
      root.addEventListener('mouseover', function (e) { var a = e.target.closest('[data-ws-tip]'); if (a) show(a); });
      root.addEventListener('mouseout', function (e) { if (e.target.closest('[data-ws-tip]')) hide(); });
      root.addEventListener('focusin', function (e) { var a = e.target.closest('[data-ws-tip]'); if (a) show(a); });
      root.addEventListener('focusout', hide);
    }

    storyBox = $('[data-ws-story]');
    if (!storyBox || !storyCards.length) return;
    $('[data-ws-story-list]', storyBox).innerHTML = storyCards.map(function (c) {
      var v = pick(c);
      return '<li class="ws-story__row' + (c.qty < 1 ? ' is-out' : '') + '"><a href="' + esc(c.url) + '" data-ws-look="' + esc(c.handle) + '" class="ws-story__img" tabindex="-1" aria-hidden="true">' + (c.img ? '<img src="' + esc(img(c.img, 122)) + '" width="61" height="85" alt="" loading="lazy">' : '') + '</a>' +
        '<span class="ws-story__name"><a href="' + esc(c.url) + '" data-ws-look="' + esc(c.handle) + '">' + esc(c.name) + '</a><small>' + money(v.price) + (v.key !== 'nonfoil' || v.cond ? ' · ' + esc(vname(v)) : '') + '</small></span>' +
        '<span data-ws-button>' + addButton(v) + '</span></li>';
    }).join('');
    storyBox.hidden = false;
    drawStoryTotals();
    storyBox.addEventListener('click', function (e) { var b = e.target.closest('[data-ws-add-all]'); if (b && b._ids) addMany(b._ids, b); });
  }

  function start() {
    var browser = $('[data-ws-browser]'), article = $('[data-ws-article]');
    if (browser) initBrowser(browser);
    if (!$('.wgt-rail, .wgt-spot') && !article) return;
    load().then(function () {
      initSpots(); initRails();
      if (article) initArticle(article);
      document.documentElement.classList.add('ws-live');
    }).catch(function () { /* the plain links in the article still work */ });
  }

  window.WGTSingles = { load: load, query: query, browseUrl: browseUrl, readState: readState };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
